import { HStack, Stack, Tabs, Text } from '@chakra-ui/react'
import { useStore } from '@tanstack/react-store'
import { getAccess } from 'features/Private'
import { useEffect } from 'react'
import { SiSublimetext } from 'react-icons/si'
import { useHistory } from 'react-router-dom'
import { useBoolean } from 'react-use'
import { ROLES } from 'shared/constants/user'
import { authStore } from 'shared/store/auth'
import { getDateTimeReadFormat } from 'shared/utils/dateFormatting'
import { Snippets, SnippetsHeading, SnippetsItem, SnippetsList } from 'uikit'
import { HeadingItem, ItemIcon, ItemLink, ItemText } from 'uikit/Snippets'
import { toaster } from 'uikit/Toaster'
import { InvitedUsersModal } from './InvitedUsersModal/InvitedUsersModal'
import { ActionButton } from './elements/ActionButton'
import { useFetchCustomer } from './services/useFetchCustomer'
import { useFetchEvents } from './services/useFetchEvents'
import { useFetchRef } from './services/useFetchRef'
import { useFetchReferralTransferCustomer } from './services/useFetchReferralTransferCustomer'
import { useTransferProcess } from './services/useTransferProcess'
import { useTransferRefund } from './services/useTransferRefund'

const role = {
  REFERRAL: 'Приглашенный',
  REFERRER: 'Пригласивший',
}

export const Referral = ({ phone }) => {
  const history = useHistory()
  const { user } = useStore(authStore)
  const isAdmin = getAccess(user.roles, [ROLES.admin])
  const { refetch: fetchCustomer, data: dataCustomer } = useFetchCustomer(phone)
  const { refetch: fetchEvents, data: dataEvents } = useFetchEvents(phone)
  const { refetch: fetchReferralTransferCustomer, data: dataTransferCustomer } =
    useFetchReferralTransferCustomer(phone)
  const { mutate: setProcess } = useTransferProcess()
  const { mutate: setRefund } = useTransferRefund()
  const { refetch: fetchRef, data: dataRef } = useFetchRef(dataCustomer)
  const [isOpenInvitedUsersModal, setOpenInvitedUsersModal] = useBoolean(false)

  const handleProcessClick = (id) => {
    setProcess(id, {
      onSuccess: async () => {
        await fetchCustomer()
        await fetchReferralTransferCustomer()
        toaster.create({
          description: 'Успешно зачислено на счет',
          type: 'success',
        })
      },
    })
  }

  const handleRefundClick = (id) => {
    setRefund(id, {
      onSuccess: async () => {
        await fetchCustomer()
        await fetchReferralTransferCustomer()
        toaster.create({
          description: 'Успешно отказано',
          type: 'success',
        })
      },
    })
  }

  useEffect(() => {
    const getData = async () => {
      try {
        await fetchCustomer()
        await fetchEvents()
        await fetchReferralTransferCustomer()
      } catch (e) {
        toaster.create({
          description: 'Пользователь не зарегистрирован в реферальной программе',
          type: 'warning',
        })
      }
    }
    if (isAdmin) {
      getData()
    }
  }, [])

  useEffect(() => {
    if (dataCustomer?.invitationCode) {
      fetchRef(dataCustomer?.invitationCode)
    }
  }, [dataCustomer])

  return (
    <>
      <HStack gap="20px" mb="30px" alignItems="flex-start">
        <Stack gap="4px" width="160px">
          <Text fontSize="0.9rem">Баланс бонусов</Text>
          <Text fontSize="1.4rem" fontWeight="600">
            {dataCustomer?.reward}
          </Text>
        </Stack>
        <Stack gap="4px" width="160px">
          <Text fontSize="0.9rem">Заблокировано</Text>
          <Text fontSize="1.4rem" fontWeight="600">
            {dataCustomer?.blockedReward}
          </Text>
        </Stack>
        <Stack gap="4px" justifyContent="center" width="190px">
          <Text fontSize="0.9rem">Зарегистрирован по коду</Text>
          {dataCustomer?.invitationCode ? (
            <Text
              fontSize="1.2rem"
              fontWeight="600"
              transition="0.3s"
              cursor="pointer"
              onClick={() => history.push(`/user/phone=${dataRef?.origin}`)}
              _hover={{
                opacity: 0.5,
              }}
            >
              {dataCustomer?.invitationCode}
            </Text>
          ) : (
            <Text fontWeight="600" fontSize="1.2rem" opacity={0.3}>
              Нет
            </Text>
          )}
        </Stack>
        <Stack gap="4px" justifyContent="center" width="190px">
          <Text fontSize="0.9rem">Приглашены по коду</Text>
          {dataCustomer?.originCode ? (
            <Text
              fontSize="1.2rem"
              fontWeight="600"
              transition="0.3s"
              cursor="pointer"
              onClick={() => setOpenInvitedUsersModal(true)}
              _hover={{
                opacity: 0.5,
              }}
            >
              {dataCustomer?.originCode}
            </Text>
          ) : (
            <Text fontWeight="600" fontSize="1.2rem" opacity={0.3}>
              Нет
            </Text>
          )}
        </Stack>
      </HStack>
      {/* <Box>123</Box> */}
      <Tabs.Root variant="subtle" colorPalette="orange" size="sm" defaultValue="transfers">
        <Tabs.List>
          <Tabs.Trigger value="transfers">Переводы</Tabs.Trigger>
          <Tabs.Trigger value="events">События</Tabs.Trigger>
        </Tabs.List>
        <Tabs.Content value="transfers" px={0}>
          <Snippets
            columns="42px 80px minmax(180px, 1fr) minmax(180px, 1fr) 180px 200px 200px 230px"
            itemWidth="1200px"
          >
            <SnippetsHeading>
              <HeadingItem />
              <HeadingItem>N</HeadingItem>
              <HeadingItem>Бонусы</HeadingItem>
              <HeadingItem>Сумма, ₽</HeadingItem>
              <HeadingItem>Дата операции</HeadingItem>
              <HeadingItem>Дата запроса</HeadingItem>
              <HeadingItem>Статус</HeadingItem>
            </SnippetsHeading>
            <SnippetsList
              activePage={0}
              totalPages={0}
              data={dataTransferCustomer}
            >
              {(item) => {
                const status = {
                  '-1': 'Отклонен',
                  0: 'Не проведен',
                  1: 'Проведен',
                }

                return (
                  <SnippetsItem>
                    <ItemIcon IconComponent={SiSublimetext} />
                    <ItemLink>{item?.rowId}</ItemLink>
                    <ItemText>{item?.amount}</ItemText>
                    <ItemText>{item?.amountRub}</ItemText>
                    <ItemText>{getDateTimeReadFormat(item?.processed)}</ItemText>
                    <ItemText>{getDateTimeReadFormat(item?.created)}</ItemText>
                    <ItemText>{status[String(item?.transferStatus)]}</ItemText>
                    <HStack gap="10px">
                      <ActionButton
                        colorPalette="green"
                        disabled={item?.transferStatus === -1 || item?.transferStatus === 1}
                        onClick={() => handleProcessClick(item?.id)}
                      >
                        Зачислить
                      </ActionButton>
                      <ActionButton
                        disabled={item?.transferStatus === -1 || item?.transferStatus === 1}
                        onClick={() => handleRefundClick(item?.id)}
                      >
                        Отклонить
                      </ActionButton>
                    </HStack>
                  </SnippetsItem>
                )
              }}
            </SnippetsList>
          </Snippets>
        </Tabs.Content>
        <Tabs.Content value="events" px={0}>
          <Snippets columns="42px minmax(400px, 1fr) 190px 220px 220px 220px" itemWidth="1600px">
            <SnippetsHeading>
              <HeadingItem />
              <HeadingItem>Событие</HeadingItem>
              <HeadingItem>Роль</HeadingItem>
              <HeadingItem>Бонусы</HeadingItem>
              <HeadingItem>Дата</HeadingItem>
              <HeadingItem>Статус</HeadingItem>
            </SnippetsHeading>
            <SnippetsList
              activePage={0}
              totalPages={0}
              data={dataEvents}
              scrollableTarget="scrollableCardContainer"
            >
              {(item, index) => (
                <SnippetsItem item={index}>
                  <ItemIcon IconComponent={SiSublimetext} />
                  <ItemText>{item?.description}</ItemText>
                  <ItemText>{role[item?.role]}</ItemText>
                  <ItemText>{`${item?.reward}`}</ItemText>
                  <ItemText>{getDateTimeReadFormat(item?.processed)}</ItemText>
                  <ItemText>{item?.isProcessed === 1 ? 'Обработан' : 'В процессе'}</ItemText>
                </SnippetsItem>
              )}
            </SnippetsList>
          </Snippets>
        </Tabs.Content>
      </Tabs.Root>
      <InvitedUsersModal
        isOpen={isOpenInvitedUsersModal}
        onClose={() => setOpenInvitedUsersModal(false)}
        originCode={dataCustomer?.originCode}
      />
    </>
  )
}
